import React from 'react';
import SelectDropdown from '../selectDropdown';
import './RechazoConfirmacionContent.css';

interface CupParaAprobar {
  id: number;
  cups: string;
  descripcion: string;
}

interface ConfirmacionAprobacionCupsProps {
  cups: CupParaAprobar[];
  tipo?: 'aprobacion' | 'rechazo';
  onCancelar: () => void;
  onConfirmarAprobacion?: () => Promise<void>;
  onConfirmarRechazo?: (motivo: string) => Promise<void>;
  onProcesandoChange?: (procesando: boolean) => void;
}

const ConfirmacionAprobacionCups: React.FC<ConfirmacionAprobacionCupsProps> = ({
  cups,
  tipo = 'aprobacion',
  onCancelar,
  onConfirmarAprobacion,
  onConfirmarRechazo,
  onProcesandoChange
}) => {
  const [motivo, setMotivo] = React.useState('');
  const [motivoPersonalizado, setMotivoPersonalizado] = React.useState('');
  const [procesando, setProcesando] = React.useState(false);

  // Notificar al padre cuando cambie el estado de procesando
  React.useEffect(() => {
    if (onProcesandoChange) {
      onProcesandoChange(procesando);
    }
  }, [procesando, onProcesandoChange]);

  const getTitle = () => {
    return tipo === 'rechazo' ? 'Estás por rechazar los siguientes CUPS' : 'Estás por aprobar los siguientes CUPS';
  };

  const getInfoText = () => {
    if (tipo === 'rechazo') {
      return 'Una vez confirmado, los CUPS quedarán registrados como rechazados y no podrá modificarse.';
    } else {
      return 'Una vez confirmado, los CUPS quedarán registrados como aprobados y no podrá modificarse.';
    }
  };

  const getConfirmButtonText = () => {
    if (procesando) {
      return tipo === 'rechazo' ? 'Rechazando...' : 'Aprobando...';
    }
    return tipo === 'rechazo' ? 'Confirmar rechazo' : 'Confirmar aprobación';
  };

  const isConfirmDisabled = () => {
    if (procesando) return true; // Deshabilitar mientras se procesa
    
    if (tipo === 'rechazo') {
      if (motivo === 'Otro') {
        return motivoPersonalizado.trim().length === 0;
      }
      return motivo.trim().length === 0;
    }
    return false;
  };

  const handleConfirm = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    // Activar estado de procesando
    setProcesando(true);
    
    try {
      if (tipo === 'rechazo' && onConfirmarRechazo) {
        const motivoFinal = motivo === 'Otro' ? motivoPersonalizado : motivo;
        await onConfirmarRechazo(motivoFinal);
      } else if (tipo === 'aprobacion' && onConfirmarAprobacion) {
        await onConfirmarAprobacion();
      }
    } finally {
      // Desactivar estado de procesando
      setProcesando(false);
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column',
      height: '100%',
      maxHeight: '80vh'
    }}>
      {/* Contenido principal con scroll */}
      <div style={{ 
        flex: '1 1 auto',
        overflowY: 'auto',
        paddingBottom: '10px',
        paddingRight: '5px'
      }}>
        <div className="rechazo-content" style={{ paddingBottom: '0' }}>
          <p className="rechazo-description">
            {getTitle()}
          </p>
          
          {/* Tabla con altura completamente dinámica */}
          <div className="cups-table-container" style={{ 
            marginBottom: '20px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            display: 'block',
            width: '100%',
            minHeight: 'auto',  // Remover min-height
            maxHeight: '25vh',  // Máximo 25vh
            overflowY: 'auto'   // Scroll cuando sea necesario
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {cups.map((cup, index) => (
                  <tr key={cup.id} style={{ 
                    backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#ffffff'
                  }}>
                    <td style={{ 
                      padding: '8px 12px', 
                      borderBottom: index < cups.length - 1 ? '1px solid #ddd' : 'none',
                      fontWeight: 'bold',
                      width: '100px',
                      fontSize: '14px',
                      borderRight: '1px solid #ddd'
                    }}>
                      {cup.cups}
                    </td>
                    <td style={{ 
                      padding: '8px 12px', 
                      borderBottom: index < cups.length - 1 ? '1px solid #ddd' : 'none',
                      fontSize: '14px'
                    }}>
                      {cup.descripcion}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Campo de motivo solo para rechazo */}
          {tipo === 'rechazo' && (
            <div className="motivo-section" style={{ marginBottom: '20px' }}>
              {/* SelectDropdown centrado */}
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '10px',
                maxWidth: '80%',
                margin: '0 auto 10px auto'
              }}>
                <SelectDropdown
                  label="Selecciona el motivo del rechazo"
                  options={[
                    { value: "Inconsistencia en porcentajes de incremento", label: "Inconsistencia en porcentajes de incremento" },
                    { value: "Falta de especificación de programas y sus incrementos", label: "Falta de especificación de programas y sus incrementos" },
                    { value: "Fechas inconsistentes o ausentes", label: "Fechas inconsistentes o ausentes" },
                    { value: "Otro", label: "Otro" }
                  ]}
                  value={motivo}
                  placeholder="Seleccione..."
                  onChange={(value) => {
                    const selectedValue = value.toString();
                    setMotivo(selectedValue);
                    if (selectedValue !== 'Otro') {
                      setMotivoPersonalizado('');
                    }
                  }}
                />
              </div>

              {/* Textarea cuando se selecciona "Otro" */}
              {motivo === 'Otro' && (
                <div>
                  <label style={{ 
                    fontSize: '14px',
                    fontWeight: 'normal',
                    display: 'block',
                    marginBottom: '5px'
                  }}>
                    Indica el motivo de rechazo <span style={{ color: '#FF6600' }}>*</span>
                  </label>
                  <textarea
                    value={motivoPersonalizado}
                    onChange={(e) => setMotivoPersonalizado(e.target.value)}
                    placeholder="Las tarifas no fueron las pactadas"
                    style={{
                      width: '100%',
                      minHeight: '60px',  // Reducido de 80px a 60px
                      maxHeight: '100px', // Máximo para evitar crecimiento excesivo
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      outline: 'none',
                      boxShadow: 'none'
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Mensaje flash azul */}
          <div className="rechazo-info-box">
            <div className="rechazo-info-icon">
              <i className="fa fa-info-circle"></i>
            </div>
            <div className="rechazo-info-text">
              {getInfoText()}
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer sticky siempre visible */}
      <div className="rechazo-footer" style={{ 
        borderTop: '1px solid #dee2e6',
        paddingTop: '15px',
        paddingBottom: '5px',
        backgroundColor: '#fff',
        marginTop: '10px',
        flexShrink: 0,
        position: 'sticky',
        bottom: 0
      }}>
        <div className="rechazo-buttons">
          {!procesando && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onCancelar();
              }}
              className="rechazo-button-cancel"
            >
              Cancelar
            </button>
          )}
          <button
            type="button"
            onClick={handleConfirm}
            className={`rechazo-button-confirm ${isConfirmDisabled() ? 'disabled' : 'enabled'} ${tipo === 'aprobacion' ? 'aprobacion' : ''}`}
            disabled={isConfirmDisabled()}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {procesando && (
              <svg
                style={{
                  animation: 'spin 1s linear infinite',
                  width: '16px',
                  height: '16px'
                }}
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeDasharray="31.4 31.4"
                  strokeLinecap="round"
                  opacity="0.25"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeDasharray="15.7 47.1"
                  strokeLinecap="round"
                  transform="rotate(-90 12 12)"
                />
              </svg>
            )}
            {getConfirmButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
};

// Agregar estilos de animación inline
if (typeof document !== 'undefined') {
  const styleId = 'confirmacion-spinner-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }
}

export default ConfirmacionAprobacionCups;